import React, { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { useLang } from "@/contexts/LanguageContext";
import { MP_DISTRICTS } from "@/lib/mpDistricts";

// Heatmap tone bands — subtle to strong saffron/emerald. Choice: saffron for
// low activity (opportunity), emerald darkening as demand climbs (coverage).
function heatBand(pct) {
  if (pct <= 0) return { bg: "bg-slate-100", text: "text-slate-500", label: "heat_none" };
  if (pct < 20) return { bg: "bg-amber-50", text: "text-amber-800", label: "heat_nascent" };
  if (pct < 40) return { bg: "bg-amber-100", text: "text-amber-900", label: "heat_growing" };
  if (pct < 60) return { bg: "bg-orange-100", text: "text-orange-900", label: "heat_active" };
  if (pct < 80) return { bg: "bg-emerald-100", text: "text-emerald-900", label: "heat_strong" };
  return { bg: "bg-emerald-200", text: "text-emerald-950", label: "heat_very" };
}

export default function GovDistrictMap() {
  const [rows, setRows] = useState([]);
  const { t } = useLang();
  useEffect(() => {
    api.get("/reports/district-demand").then((r) => setRows((r.data || []).filter((d) => d.district))).catch(() => {});
  }, []);

  // Fill in every one of the 55 MP districts, even if they have zero demand,
  // so the heatmap surfaces coverage gaps rather than hiding them.
  const enriched = useMemo(() => {
    const byName = Object.fromEntries(rows.map((r) => [r.district, r]));
    return MP_DISTRICTS.map((d) => byName[d] || { district: d, total_amount: 0, count: 0 });
  }, [rows]);

  const sortedByDemand = useMemo(
    () => [...enriched].sort((a, b) => (b.total_amount || 0) - (a.total_amount || 0)),
    [enriched]
  );
  const sortedByName = useMemo(() => [...enriched].sort((a, b) => a.district.localeCompare(b.district)), [enriched]);
  const max = Math.max(...enriched.map((r) => r.total_amount || 0), 1);
  const withActivity = enriched.filter((r) => (r.total_amount || 0) > 0).length;

  return (
    <div className="space-y-6" data-testid="gov-map">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 brand-heading">{t("district_map")}</h1>
        <p className="text-slate-600 mt-1 text-sm">
          {t("gm_sub_a")} {MP_DISTRICTS.length - withActivity} {t("gm_sub_b")}
        </p>
      </div>

      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <h2 className="font-semibold text-slate-900 brand-heading mb-3">{t("gm_chart")}</h2>
        <ResponsiveContainer width="100%" height={420}>
          <BarChart data={sortedByDemand.slice(0, 20)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="district" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={80} interval={0} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatINR(v)} width={80} />
            <Tooltip formatter={(v) => [formatINR(v), t("g_demand")]} />
            <Bar dataKey="total_amount" name={t("g_demand")} fill="#ea580c" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Heatmap tile grid — every one of the 55 districts, colour-shaded by demand intensity */}
      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-semibold text-slate-900 brand-heading">{t("gm_heat")} {MP_DISTRICTS.length} {t("gm_heat_b")}</h2>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500" data-testid="heat-legend">
            <span>{t("gm_low")}</span>
            <span className="w-4 h-3 rounded bg-slate-100 inline-block" />
            <span className="w-4 h-3 rounded bg-amber-50 inline-block" />
            <span className="w-4 h-3 rounded bg-amber-100 inline-block" />
            <span className="w-4 h-3 rounded bg-orange-100 inline-block" />
            <span className="w-4 h-3 rounded bg-emerald-100 inline-block" />
            <span className="w-4 h-3 rounded bg-emerald-200 inline-block" />
            <span>{t("gm_high")}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="district-heat-grid">
          {sortedByName.map((r) => {
            const pct = Math.round(((r.total_amount || 0) / max) * 100);
            const band = heatBand(pct);
            return (
              <div
                key={r.district}
                data-testid={`heat-tile-${r.district}`}
                title={`${r.district} · ${formatINR(r.total_amount || 0)} across ${r.count || 0} enterprises`}
                className={`p-3 rounded-md border border-slate-200 flex items-center justify-between gap-2 ${band.bg}`}
              >
                <div className="min-w-0">
                  <div className={`text-sm font-semibold truncate ${band.text}`}>{r.district}</div>
                  <div className="text-[10px] opacity-70 truncate">{r.count || 0} {t("gm_msmes")} · {t(band.label)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[11px] font-bold text-slate-900">{r.total_amount ? formatINR(r.total_amount) : "—"}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <DisclaimerBanner compact />
    </div>
  );
}
